import React, { useEffect, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { useSelector } from 'react-redux';
import { DNAText, DNABox, Icon, DNAIcon } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { useAppSettings } from 'src/state/context/AppSettings';
import { RootState } from 'src/state/redux';
import { getZendeskTokenPayload } from 'src/utils/zendeskHelpers';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { ZENDESK_FEATURE } from '@alucio/aws-beacon-amplify/src/API';

type ZendeskWidget = (...args: unknown[]) => void;
type NewRelicAgent = {
  noticeError: (error: Error, attributes?: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    zE?: ZendeskWidget;
    newrelic?: NewRelicAgent;
    __alucioZendeskUnavailableReported?: boolean;
  }
}

const getZendeskSnippetDiagnostics = () => {
  if (typeof document === 'undefined') {
    return {
      zendesk_snippet_status: 'unavailable',
      zendesk_snippet_present: false,
    };
  }

  const snippet = document.getElementById('ze-snippet');

  return {
    zendesk_snippet_status: snippet?.getAttribute('data-zendesk-status') ?? (snippet ? 'pending' : 'missing'),
    zendesk_snippet_present: Boolean(snippet),
  };
};

const reportZendeskFailure = (message: string, command: string, error?: unknown) => {
  if (typeof window === 'undefined' || window.__alucioZendeskUnavailableReported || !window.newrelic) return;

  window.__alucioZendeskUnavailableReported = true;
  window.newrelic.noticeError(new Error(message), {
    integration: 'zendesk',
    zendesk_command: command,
    ...(error instanceof Error ? { zendesk_error_type: error.name } : {}),
    ...getZendeskSnippetDiagnostics(),
    host: window.location.host,
  });
};

const sendZendeskCommand = (command: string, ...args: unknown[]) => {
  if (typeof window === 'undefined') return;

  if (typeof window.zE !== 'function') {
    reportZendeskFailure('Zendesk widget unavailable', command);
    return;
  }

  try {
    window.zE(command, ...args);
  } catch (error) {
    reportZendeskFailure('Zendesk command failed', command, error);
  }
};

const sharedStyles = StyleSheet.create({
  notificationIcon: {
    width: 8,
    height: 8,
    color: colors['color-danger-500'],
  },
})

export const useChatButtonSharedResources = (chatEnabled: boolean) => {
  const { isOnline } = useAppSettings();
  const { isOpen:CPMisOpen } = useSelector((state: RootState) => state.contentPreviewModal)
  const [unreadMessages, setUnreadMessages] = useState<boolean>()
  const canUseZendesk = isOnline && chatEnabled;
  const openChat = () => {
    if (!canUseZendesk) return;

    sendZendeskCommand('messenger', 'open');
  }

  /** TODO: Consider moving some of this stuff to a more central/general
 * zendesk related management facility such as a context or hook */
  useEffect(() => {
    if (!canUseZendesk) return;

    const updatedIndex = CPMisOpen ? -1 : 999999
    sendZendeskCommand('messenger:set', 'zIndex', updatedIndex);
  }, [CPMisOpen, canUseZendesk])

  /** Component init */
  useEffect(() => {
    if (!canUseZendesk) return;

    /** Login User */
    sendZendeskCommand('messenger', 'loginUser', async (cb: (token: string) => void) => {
      const payload = await getZendeskTokenPayload(ZENDESK_FEATURE.CHAT)
      cb(payload.token)
    });

    /** Setup unread message event handler */
    /** TODO: Identify approach for cleaning up this handler. It is currently causing a memory leak when logging out and back in again. */
    sendZendeskCommand('messenger:on', 'unreadMessages', (count: number) => {
      setUnreadMessages(count > 0)
    })

    /** On component destroy, logout the user */
    return () => {
      sendZendeskCommand('messenger', 'logoutUser')
    }
  }, [canUseZendesk])

  return {
    openChat,
    unreadMessages,
  }
}

const ChatButton: React.FC = () => {
  const enableZendeskChat = useFeatureFlags('enableZendeskChat');
  const { openChat, unreadMessages } = useChatButtonSharedResources(enableZendeskChat)
  const { deviceMode } = useAppSettings()
  const { notificationIcon } = sharedStyles

  return (
    <TouchableOpacity
      onPress={() => openChat()}
      activeOpacity={1}
      testID="account-chat-button"
    >
      <DNABox
        style={{ padding: 12 }}
        alignY="center"
        spacing={!unreadMessages ? 'sm' : 'xs'}
      >
        <DNABox>
          <DNAIcon.Styled
            name="chat-text"
            status="tertiary"
            size={deviceMode === 'desktop' ? 'md' : 'xl'}
          />
          {unreadMessages && <Icon name={'record'} style={notificationIcon}/>}
        </DNABox>
        <DNAText>Chat support</DNAText>
      </DNABox>
    </TouchableOpacity>

  )
}

export default ChatButton
